import { Banknote } from "lucide-react";
import { hasPriceInfo, priceBadgeText, type RoomPricingLike } from "./pricing";

export interface RoomPriceBadgeProps {
  /** The room's ``pricing`` from the API. Renders nothing for a free room (``null``, or unpaid without an
   *  upcoming price). */
  pricing: RoomPricingLike | null | undefined;
  /** ``sm`` for cards and lists, ``md`` for a room page header, ``xl`` for a kiosk read across the room. */
  size?: "sm" | "md" | "xl";
}

const SIZES = {
  sm: { font: 12, icon: 13, pad: "2px 8px", gap: 4 },
  md: { font: 14, icon: 16, pad: "4px 10px", gap: 6 },
  xl: { font: 30, icon: 34, pad: "10px 22px", gap: 12 },
} as const;

/** The price of a paid meeting room as a pill: "500 kr/h excl. VAT" — or, while its price has not
 *  started yet, "Free until 1 Nov, then 500 kr/h excl. VAT". Mantine tokens only, so it follows the
 *  colour scheme and a branded theme. */
export function RoomPriceBadge({ pricing, size = "sm" }: RoomPriceBadgeProps) {
  if (!hasPriceInfo(pricing)) return null;
  const text = priceBadgeText(pricing);
  if (!text) return null;
  const s = SIZES[size];
  const color = pricing!.paid ? "orange" : "teal";
  return (
    <span
      data-testid="room-price-badge"
      title={pricing!.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: s.gap,
        maxWidth: "100%",
        padding: s.pad,
        borderRadius: 999,
        fontSize: s.font,
        fontWeight: 600,
        lineHeight: 1.25,
        color: `var(--mantine-color-${color}-light-color)`,
        background: `var(--mantine-color-${color}-light)`,
        border: `1px solid color-mix(in srgb, var(--mantine-color-${color}-text) 60%, transparent)`,
        whiteSpace: "normal",
      }}
    >
      <Banknote size={s.icon} style={{ flexShrink: 0 }} aria-hidden />
      <span>{text}</span>
    </span>
  );
}
